import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { connect } from 'react-redux';

import { getPlatformDetailsSelector } from '../../platform/platformSelector';
import { isMobileMode, isPlatformIframe } from '../../platform/utils/platformDetailsUtils';
import { setGridSize } from './gridActions';
import { setGridSizeLocalStorage } from './gridSizeStorageService';
import { getGridSizeBodyClassName, getPlatformGridSize, isMobileGridName } from './gridUtils';

import { prop } from '../../../common/utils/immutableHelper';

import { GRID_SIZE_LIST } from './gridConstants';

const mapStateToProps = (state) => ({
    gridSize: state.getIn(['app', 'gridSize']),
    platformDetails: getPlatformDetailsSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
    updateGridSize: (size, name) => dispatch(setGridSize(size, name)),
});

const getSizeName = prop('name');
const getSizeInitialized = prop('initialized');

const ALL_GRID_SIZE_CLASS_NAMES = GRID_SIZE_LIST.map((gridSize) => getGridSizeBodyClassName(gridSize.name));

/**
 * The mobile grid is never persisted, on any platform: the mobile workspace always uses it, so there is nothing
 * to remember, and storing it would overwrite the grid size the user chose for the desktop workspace — which
 * that workspace then cannot restore, because `getPlatformGridSize` refuses a stored mobile grid and falls back
 * to the display size. Entering the mobile workspace is remembered separately, by `lastAppMode`, so platform
 * initialisation no longer needs a stored mobile grid as its signal.
 */
const shouldPersistGridSize = (platformDetails, sizeName) => {
    if (isPlatformIframe(platformDetails)) return false;
    if (isMobileGridName(sizeName)) return false;

    return true;
};

export const GridSizeManager = ({ gridSize, updateGridSize, platformDetails }) => {
    const isMobileWorkspace = isMobileMode(platformDetails);

    const removeBodyClass = () => {
        document.body.classList.remove(...ALL_GRID_SIZE_CLASS_NAMES);
        document.documentElement.classList.remove(...ALL_GRID_SIZE_CLASS_NAMES);
    };

    const setBodyClass = (sizeName) => {
        const className = getGridSizeBodyClassName(sizeName);

        removeBodyClass();
        document.body.classList.add(className);
        document.documentElement.classList.add(className);
    };

    // on grid size change
    useEffect(() => {
        if (!getSizeInitialized(gridSize)) return;

        const sizeName = getSizeName(gridSize);

        if (sizeName) {
            setBodyClass(sizeName);
            if (shouldPersistGridSize(platformDetails, sizeName)) setGridSizeLocalStorage(sizeName);
        }

        return removeBodyClass;
    }, [gridSize, platformDetails]);

    // Keep the grid in step with the workspace: on mount, and whenever the app moves between the mobile
    // and desktop workspaces (a Stage Manager resize, a fold, or the "switch canvas view" action).
    // Only a grid that contradicts the workspace is replaced, so a size chosen within a workspace sticks.
    useEffect(() => {
        const isGridMobile = isMobileGridName(getSizeName(gridSize));
        if (getSizeInitialized(gridSize) && isGridMobile === isMobileWorkspace) return;

        const { size, name } = getPlatformGridSize(platformDetails);
        updateGridSize(size, name);
    }, [isMobileWorkspace, gridSize, platformDetails, updateGridSize]);

    return null;
};

GridSizeManager.propTypes = {
    gridSize: PropTypes.object,
    updateGridSize: PropTypes.func,
    platformDetails: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(GridSizeManager);
